import { useEffect, useRef, useState } from "react";
import "react-international-phone/style.css";
import "./style.scss";
import { useTranslation } from "react-i18next";
import { KYC_Step } from "entities/account";
import { VerificationBtn } from "../components/button";
import Mail from "../../../../assets/verification/Mail.svg";
import MailVerify from "../../../../assets/verification/MailVerify.svg";
import Edit from "../../../../assets/verification/Edit.svg";
import { useVerificationStep } from "hooks/useVerificationStep";
import { useStore } from "zustand";
import { sessionModel } from "entities/session";
import OtpInput from "widgets/otp-input";
import { PATH_LIST } from "shared/lib/react-router";
import { useNavigate } from "react-router-dom";
import { EntityType } from "static/types";

const isEmailValid = (email: string): boolean => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([^<>()[\]\\.,;:\s@"]+\.)+[^<>()[\]\\.,;:\s@"]{2,})$/i;
  return re.test(String(email).toLowerCase());
};

export const EmailPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("onboarding-auth");
  const { t: o } = useTranslation("onboarding");
  const { t: y } = useTranslation("yup");
  const { user, entity } = useStore(sessionModel.sessionStore);

  const [email, setEmail] = useState<string>("");
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<boolean>(false);

  const [timer, setTimer] = useState<number>(59);
  const [resend, setResend] = useState<boolean>(false);

  const [result, setResult] = useState<string>("");
  const [error, setError] = useState<boolean>(false);

  const isValid = isEmailValid(email);

  const { isLoading, onSubmit } =
    useVerificationStep<KYC_Step.EmailVerification>({
      step: KYC_Step.EmailVerification,
      navigateOnSuccess: "",
      onSuccess: () => {
        setEmailSent(true);
        setResend(false);
        setTimer(59);
      },
      onError: !emailSent
        ? undefined
        : () => {
            setError(true);
          },
    });

  useEffect(() => {
    if (user?.requestedEmail) setEmail(user.requestedEmail);
    else if (user?.email) setEmail(user.email);
    else setEmail("");
  }, [user?.requestedEmail, user?.email]);

  useEffect(() => {
    if (!resend && emailSent && timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);

      return () => clearInterval(interval);
    } else if (!resend && emailSent && timer === 0) {
      setResend(true);
    }
  }, [emailSent, resend, timer]);

  const handleOnChange = (newValue: string) => {
    setResult(newValue);
    setError(false);

    if (newValue.length === 4) {
      onSubmit(
        {
          email,
          code: newValue,
        },
        user?.isPhoneVerified
          ? entity === EntityType.Entity
            ? PATH_LIST.businessDocuments
            : PATH_LIST.uploadCredentials
          : PATH_LIST.phoneNumber
      );
    }
  };

  const isFilled = result.length === 4;

  return (
    <div className="verification-email">
      <div className="verification-email-main">
        <div className="verification-email-top-wrapper">
          <img src={emailSent ? MailVerify : Mail} alt="Mail" />
          <p>{emailSent ? o("emailSent") : o("verifyEmail")}</p>
          <span>{emailSent ? o("codeSentToEmail") : o("emailVerificationCode")}</span>
          {!emailSent && (
            <div className="email-and-edit">
              <label htmlFor="email">{t("email")}</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => {
                  const newEmail = e.target.value;
                  setEmail(newEmail);
                  setEmailError(!isEmailValid(newEmail.trim()));
                }}
                placeholder="example.@email.com"
                autoFocus
              />

              {/* 
              <FormField
                customClass="
                type="email"
                id="email"
                label={t("email")}
                autoComplete="off"
                isInvalid={emailError}
                placeholder={t("enterYourEmail")}
              /> */}

              {emailError && (
                <span className="email-validation">{y("invalidEmail")}</span>
              )}
            </div>
          )}
        </div>

        {emailSent && (
          <div className="verification-email-field-wrapper">
            <OtpInput
              value={result}
              onChange={handleOnChange}
              disabled={isLoading}
              error={error}
              isFilled={isFilled}
              shouldAutoFocus={true}
            />

            {error && <span className="error-verify">{y("codeInvalid")}</span>}
            {timer > 0 && (
              <span className="resend-code">
                {t("resendCode")} {timer} {o("sec")}
              </span>
            )}
            <span className="resend-code">{t("didntGetEmail")}</span>
          </div>
        )}
      </div>

      <VerificationBtn
        disabled={
          isLoading ||
          (emailSent ? !resend : !email.length || !isValid || emailError)
        }
        onClick={() => {
          email.trim() === user?.email && user?.isEmailVerified
            ? navigate(
                user?.isPhoneVerified
                  ? entity === EntityType.Entity
                    ? PATH_LIST.businessDocuments
                    : PATH_LIST.uploadCredentials
                  : PATH_LIST.phoneNumber
              )
            : onSubmit({
                email,
                code: "",
              });
        }}
        text={emailSent ? t("resendCode") : t("continue")}
      />
    </div>
  );
};
